import React, { useState, useEffect } from 'react';

const CustomerDetailsManagement = () => {
    const [customerDetails, setCustomerDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all'); // all, completed, pending

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        fetchCustomerDetails();
    }, []);

    const fetchCustomerDetails = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${backendUrl}/api/admin/customer-details`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch customer details');
            }

            const data = await response.json();
            setCustomerDetails(data.customer_details || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const sendReminder = async (bookingReference) => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${backendUrl}/api/admin/send-reminder/${bookingReference}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (data.success) {
                alert('Reminder email sent successfully!');
                fetchCustomerDetails(); // Refresh data
            } else {
                alert(data.message || 'Failed to send reminder');
            }
        } catch (err) {
            alert('Error sending reminder: ' + err.message);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (detail) => {
        if (detail.form_completed) {
            return <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">Completed</span>;
        }
        
        const adventureDate = new Date(detail.booking_info.booking_date);
        const today = new Date();
        const daysUntilAdventure = Math.ceil((adventureDate - today) / (1000 * 60 * 60 * 24));
        
        if (daysUntilAdventure <= 2) {
            return <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded-full">Urgent</span>;
        } else if (detail.reminder_sent) {
            return <span className="px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">Reminded</span>;
        } else {
            return <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">Pending</span>;
        }
    };

    const filteredDetails = customerDetails.filter(detail => {
        if (filter === 'completed') return detail.form_completed;
        if (filter === 'pending') return !detail.form_completed;
        return true;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Customer Details</h2>
                <button
                    onClick={() => fetchCustomerDetails()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    Refresh
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {/* Filter Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    {[
                        { key: 'all', label: 'All', count: customerDetails.length },
                        { key: 'pending', label: 'Pending', count: customerDetails.filter(d => !d.form_completed).length },
                        { key: 'completed', label: 'Completed', count: customerDetails.filter(d => d.form_completed).length }
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                filter === tab.key
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {tab.label} ({tab.count})
                        </button>
                    ))}
                </nav>
            </div>

            {/* Customer Details Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Customer
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Adventure
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Details
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredDetails.map((detail) => (
                            <tr key={detail.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {detail.customer_name}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {detail.customer_email}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {detail.booking_reference}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900">
                                        {detail.booking_info.adventure_title}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {detail.booking_info.participants} person(s)
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {detail.booking_info.booking_date}
                                </td>
                                <td className="px-6 py-4">
                                    {detail.form_completed ? (
                                        <div className="space-y-1">
                                            <div className="text-sm text-gray-900">
                                                <span className="font-medium">Height:</span> {detail.height || 'Not provided'}
                                            </div>
                                            <div className="text-sm text-gray-900">
                                                <span className="font-medium">Shirt:</span> {detail.shirt_size || 'Not provided'}
                                            </div>
                                            <div className="text-sm text-gray-900">
                                                <span className="font-medium">Shoe:</span> {detail.shoe_size || 'Not provided'}
                                            </div>
                                            {detail.allergies && (
                                                <div className="text-sm text-red-600">
                                                    <span className="font-medium">⚠️ Allergies:</span> {detail.allergies}
                                                </div>
                                            )}
                                            {detail.dietary_requirements && (
                                                <div className="text-sm text-green-600">
                                                    <span className="font-medium">🥗 Dietary:</span> {detail.dietary_requirements}
                                                </div>
                                            )}
                                            <div className="text-xs text-gray-500">
                                                Completed: {formatDate(detail.completed_at)}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-500">
                                            Details not provided yet
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getStatusBadge(detail)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-y-2">
                                    {!detail.form_completed && (
                                        <>
                                            <button
                                                onClick={() => sendReminder(detail.booking_reference)}
                                                className="block w-full px-3 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700"
                                            >
                                                Send Reminder
                                            </button>
                                            {detail.reminder_sent && (
                                                <div className="text-xs text-gray-500">
                                                    Last sent: {formatDate(detail.reminder_sent_at)}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <a
                                        href={`/customer-details/${detail.booking_reference}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block w-full px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 text-center"
                                    >
                                        View Form
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredDetails.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-gray-500 text-lg">No customer details found</div>
                        <div className="text-gray-400 text-sm mt-2">
                            Customer details will appear here after bookings are made
                        </div>
                    </div>
                )}
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Completion Rate</h3>
                    <div className="text-3xl font-bold text-green-600">
                        {customerDetails.length > 0 
                            ? Math.round((customerDetails.filter(d => d.form_completed).length / customerDetails.length) * 100)
                            : 0
                        }%
                    </div>
                    <div className="text-sm text-gray-500">
                        {customerDetails.filter(d => d.form_completed).length} of {customerDetails.length} completed
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Pending Forms</h3>
                    <div className="text-3xl font-bold text-orange-600">
                        {customerDetails.filter(d => !d.form_completed).length}
                    </div>
                    <div className="text-sm text-gray-500">
                        Forms awaiting completion
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Reminders Sent</h3>
                    <div className="text-3xl font-bold text-blue-600">
                        {customerDetails.filter(d => d.reminder_sent).length}
                    </div>
                    <div className="text-sm text-gray-500">
                        Reminder emails sent
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerDetailsManagement;